/*
 * FuelBox Carb-Planner PDF export.
 * Requires the locally hosted jsPDF UMD build to expose window.jspdf.jsPDF.
 */
(() => {
  "use strict";

  const LABELS = Object.freeze({
    sport: { laufen: "Laufen", rad: "Rad", trail: "Trail / Ultra", tri: "Triathlon" },
    context: { training: "Training", race: "Wettkampf" },
    intensity: { locker: "Locker", moderat: "Moderat", hart: "Hart" },
    toleranceBand: {
      unknown: "Noch nicht sicher",
      tested30: "30 g/h getestet",
      tested45: "45 g/h getestet",
      tested60: "60 g/h getestet",
      tested90: "90 g/h getestet"
    },
    conditions: { kuehl: "Kühl", mild: "Gemässigt", heiss: "Heiss / schwül" },
    formats: { gel: "Gels", drink: "Drink-Mix", chews: "Chews", riegel: "Energy-Riegel" }
  });

  const COLORS = Object.freeze({
    espresso: [20, 10, 5],
    terracotta: [122, 70, 46],
    caramel: [166, 96, 63],
    amber: [236, 161, 95],
    peach: [250, 231, 214],
    cream: [253, 249, 246],
    sand: [242, 232, 224],
    oat: [248, 244, 241],
    white: [255, 255, 255],
    muted: [101, 91, 85],
    line: [221, 211, 205],
    warning: [196, 80, 53],
    warningFill: [253, 239, 235]
  });

  const DISCLAIMER = "Teste Verpflegung, Trinkstrategie und neue Produkte zuerst im Training. Steigere Kohlenhydratmengen schrittweise. Die Angaben sind Richtwerte für gesunde Erwachsene und ersetzen keine individuelle medizinische oder ernährungswissenschaftliche Beratung.";
  const PLANNER_URL = "https://fuelbox.ch/pages/carb-planner";

  const array = value => Array.isArray(value) ? value : Array.from(value || []);
  const formatDuration = minutes => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")} h`;
  const formatTimelineMinute = minute => minute < 60
    ? `${minute} min`
    : `${Math.floor(minute / 60)}:${String(minute % 60).padStart(2, "0")} h`;
  const formatNumber = value => Number(value).toLocaleString("de-CH", { maximumFractionDigits: 1 });
  const safeLabel = (group, value) => LABELS[group]?.[value] || String(value || "-");
  const dateLabel = date => new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);

  function toleranceLabel(input) {
    const exact = input.expertEnabled && input.expert?.exactCarbRateEnabled && Number.isFinite(input.expert.testedCarbRateGph);
    return exact ? `${input.expert.testedCarbRateGph} g/h persönlich getestet` : safeLabel("toleranceBand", input.toleranceBand);
  }

  function buildHydration(plan) {
    const hydration = plan.hydration;
    if (hydration.mode === "thirst") {
      return {
        summary: "Nach Durst",
        detail: "Für diese kurze Einheit ist normalerweise kein Liter-pro-Stunde-Ziel nötig.",
        safety: plan.input.conditions === "heiss"
          ? "Halte trotzdem Flüssigkeit bereit und starte gut hydriert."
          : "Trinke entsprechend Durst, Erfahrung und den realen Bedingungen."
      };
    }

    const sodium = hydration.sodiumGuidance === "400_700_MG_PER_L"
      ? " Ein Getränk mit ungefähr 400–700 mg Natrium pro Liter kann praktisch sein."
      : "";
    const total = hydration.showTotal
      ? `${formatNumber(hydration.fluidTotalMinL)}–${formatNumber(hydration.fluidTotalMaxL)} L ungefähr über die gesamte Dauer.`
      : "Bei mehr als vier Stunden Nachfüllen planen und die Spanne nicht als vollständig mitzuführende Gesamtmenge verstehen.";

    return {
      summary: `${formatNumber(hydration.fluidRateMinLph)}–${formatNumber(hydration.fluidRateMaxLph)} L/h`,
      detail: `Als Startbereich.${sodium} ${total}`,
      safety: "Der Bereich ist eine Startorientierung, kein Trinkziel. Trinke nach Durst, Erfahrung und realen Bedingungen. Natrium macht übermässiges Trinken nicht sicher."
    };
  }

  function buildPdfModel(plan, options = {}) {
    if (!plan || !["fuel", "no_fuel"].includes(plan.status)) throw new Error("INVALID_PLAN_FOR_PDF");

    const createdAt = options.createdAt instanceof Date ? options.createdAt : new Date();
    const automaticFormats = array(plan.formats?.recommendedFormats).map(value => safeLabel("formats", value));
    const personalFormats = plan.formats?.productFormatMode === "manual"
      ? array(plan.formats?.personalFormats).map(value => safeLabel("formats", value))
      : [];
    const noFuelTitle = plan.noFuelReason === "UNDER_45" ? "Kein Fuelling nötig" : "Fuelling optional";
    const noFuelText = plan.noFuelReason === "UNDER_45"
      ? "Für diese kurze Einheit ist normalerweise kein Fuelling während der Belastung nötig."
      : "Für diese lockere Übergangseinheit ist keine feste Kohlenhydratmenge während der Belastung eingeplant.";
    const timelineRows = plan.status === "fuel"
      ? array(plan.timeline?.intakes).filter(item => item.carbsG > 0).map(item => ({
        time: formatTimelineMinute(item.minuteDisplay),
        carbs: `${item.carbsG} g`
      }))
      : [];
    const metricTotalLabel = plan.timeline?.mode === "ultra_pattern" ? "3-h-Muster" : "Gesamtmenge";
    const metricRhythm = plan.status === "fuel"
      ? (plan.timeline.intakeCount === 1 ? "1 Einnahme" : `ca. alle ${plan.timeline.displayIntervalMinutes} min`)
      : "Nicht erforderlich";
    const statusSlug = plan.status === "fuel" ? `${plan.carbTarget.planRateGph}gph` : "no-fuel";
    const durationSlug = `${Math.floor(plan.input.durationMinutes / 60)}h${String(plan.input.durationMinutes % 60).padStart(2, "0")}`;

    return {
      title: "Dein Carb-Plan",
      createdLabel: `Erstellt am ${dateLabel(createdAt)}`,
      plannerUrl: options.plannerUrl || PLANNER_URL,
      fileName: `fuelbox-carb-plan-${plan.input.sport}-${durationSlug}-${statusSlug}.pdf`,
      status: plan.status,
      inputItems: [
        ["Sport", safeLabel("sport", plan.input.sport)],
        ["Anlass", safeLabel("context", plan.input.context)],
        ["Dauer", formatDuration(plan.input.durationMinutes)],
        ["Intensität", safeLabel("intensity", plan.input.intensity)],
        ["Verträglichkeit", toleranceLabel(plan.input)],
        ["Bedingungen", safeLabel("conditions", plan.input.conditions)]
      ],
      statusCard: plan.status === "no_fuel" ? { title: noFuelTitle, text: noFuelText } : null,
      metrics: plan.status === "fuel" ? [
        ["Planwert", `${plan.carbTarget.planRateGph} g/h`],
        [metricTotalLabel, `${plan.timeline.plannedTotalCarbs} g`],
        ["Rhythmus", metricRhythm]
      ] : [],
      timeline: {
        title: plan.timeline?.mode === "ultra_pattern" ? "Fuelling-Muster für die ersten 3 Stunden" : "Fuelling-Timeline",
        note: plan.timeline?.mode === "ultra_pattern"
          ? "Dieses 3-Stunden-Muster wiederholen und unterwegs an Verträglichkeit sowie reale Bedingungen anpassen. Es ist bewusst keine scheinbar exakte Gesamtplanung für den gesamten Ultra."
          : "Die Grammwerte ergeben zusammen die gerundete Gesamtmenge des Plans.",
        rows: timelineRows
      },
      automaticFormats,
      personalFormats,
      productNote: plan.status === "no_fuel"
        ? "Für diesen Plan ist keine Produktform erforderlich."
        : "Drink-Mix kann einen Teil oder das gesamte Kohlenhydratziel abdecken. Nur enthaltene Kohlenhydrate zählen zum Stundenziel; die Trinkmenge weiterhin nach Durst, Erfahrung und Bedingungen wählen. Produktformen und Mengen zuerst im Training testen.",
      guidance: [
        ["Vorher", [plan.guidance?.beforeText, plan.guidance?.beforeExtra].filter(Boolean).join(" ")],
        ["Während", [plan.guidance?.duringText, plan.guidance?.duringExtra].filter(Boolean).join(" ")],
        ["Nachher", [plan.guidance?.afterText, plan.guidance?.afterExtra].filter(Boolean).join(" ")]
      ],
      hydration: buildHydration(plan),
      warning: plan.primaryWarning ? { title: plan.primaryWarning.title, text: plan.primaryWarning.text } : null,
      disclaimer: DISCLAIMER
    };
  }

  function generatePdf(plan, options = {}) {
    const JsPdf = globalThis.jspdf?.jsPDF;
    if (!JsPdf) throw new Error("JSPDF_NOT_LOADED");
    const model = buildPdfModel(plan, options);
    const doc = new JsPdf({ orientation: "portrait", unit: "mm", format: "a4", compress: true, putOnlyUsedFonts: true });
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 16;
    const contentWidth = pageWidth - margin * 2;
    const footerY = 286;
    let y = 17;

    const setTextColor = color => doc.setTextColor(...color);
    const setFillColor = color => doc.setFillColor(...color);
    const setDrawColor = color => doc.setDrawColor(...color);
    const lineHeightMm = (fontSize, factor = 1.35) => fontSize * 0.3528 * factor;

    function addPage(variant = "continuation") {
      doc.addPage();
      y = 17;
      if (variant === "guide") drawGuideHeader();
      else drawContinuationHeader();
    }

    function ensureSpace(height) {
      if (y + height > footerY - 5) addPage();
    }

    function drawTextBlock(text, config = {}) {
      const value = String(text || "").trim();
      if (!value) return 0;
      const fontSize = config.fontSize || 9.5;
      const width = config.width || contentWidth;
      const x = config.x ?? margin;
      const font = config.font || "helvetica";
      const style = config.style || "normal";
      const color = config.color || COLORS.espresso;
      const factor = config.lineHeightFactor || 1.35;
      doc.setFont(font, style);
      doc.setFontSize(fontSize);
      setTextColor(color);
      const lines = doc.splitTextToSize(value, width);
      const height = Math.max(1, lines.length) * lineHeightMm(fontSize, factor);
      ensureSpace(height + (config.after || 0));
      doc.text(lines, x, y, { lineHeightFactor: factor });
      y += height + (config.after || 0);
      return height;
    }

    function drawSectionTitle(title) {
      ensureSpace(13);
      const top = y + 3;
      const barHeight = 8.5;
      const titleSize = title.length > 38 ? 11.2 : 12;
      setFillColor(COLORS.amber);
      doc.roundedRect(margin, top, 1.7, barHeight, 0.85, 0.85, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(titleSize);
      setTextColor(COLORS.espresso);
      doc.text(title, margin + 5, top + 6.2);
      y = top + barHeight + 3;
    }

    function drawContinuationHeader() {
      setFillColor(COLORS.espresso);
      doc.roundedRect(margin, y, contentWidth, 18, 3, 3, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      setTextColor(COLORS.white);
      doc.text("FuelBox", margin + 6, y + 8);
      doc.setFontSize(8.5);
      setTextColor(COLORS.amber);
      doc.text("DEIN CARB-PLAN · FORTSETZUNG", pageWidth - margin - 6, y + 8, { align: "right" });
      y += 25;
    }

    function drawGuideHeader() {
      setFillColor(COLORS.espresso);
      doc.roundedRect(margin, y, contentWidth, 31, 3, 3, "F");
      setFillColor(COLORS.amber);
      doc.roundedRect(margin + 7, y + 6, 31, 7, 3.5, 3.5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      setTextColor(COLORS.espresso);
      doc.text("PRAXIS-GUIDE", margin + 22.5, y + 10.7, { align: "center" });
      doc.setFontSize(18);
      setTextColor(COLORS.white);
      doc.text("Vorher. Während. Danach.", margin + 7, y + 23);
      doc.setFontSize(10);
      setTextColor(COLORS.peach);
      doc.text("Dein Carb-Plan", pageWidth - margin - 7, y + 10.5, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.4);
      doc.text("Einfach mitnehmen und Schritt für Schritt anwenden.", pageWidth - margin - 7, y + 20.5, { align: "right" });
      y += 39;
    }

    function drawInfoRows(rows) {
      const columnWidth = (contentWidth - 6) / 2;
      const cellHeight = 15;
      rows.forEach((row, index) => {
        const column = index % 2;
        if (column === 0) ensureSpace(cellHeight + 3);
        const rowIndex = Math.floor(index / 2);
        const baseY = y + (rowIndex % 1) * cellHeight;
        const x = margin + column * (columnWidth + 6);
        setFillColor(COLORS.oat);
        setDrawColor(COLORS.line);
        doc.roundedRect(x, baseY, columnWidth, cellHeight - 2, 2, 2, "FD");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        setTextColor(COLORS.muted);
        doc.text(String(row[0]), x + 4, baseY + 5);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.3);
        setTextColor(COLORS.espresso);
        const valueLines = doc.splitTextToSize(String(row[1]), columnWidth - 8).slice(0, 2);
        doc.text(valueLines, x + 4, baseY + 10, { lineHeightFactor: 1.1 });
        if (column === 1 || index === rows.length - 1) y += cellHeight;
      });
      y += 2;
    }

    function drawMetrics(metrics) {
      const gap = 4;
      const width = (contentWidth - gap * 2) / 3;
      const height = 24;
      ensureSpace(height + 6);
      metrics.forEach((metric, index) => {
        const x = margin + index * (width + gap);
        setFillColor(index === 0 ? COLORS.peach : COLORS.oat);
        setDrawColor(index === 0 ? COLORS.amber : COLORS.line);
        doc.roundedRect(x, y, width, height, 2, 2, "FD");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.4);
        setTextColor(COLORS.muted);
        doc.text(metric[0], x + 4, y + 6);
        doc.setFont("courier", "bold");
        doc.setFontSize(index === 0 ? 15 : 12);
        setTextColor(index === 0 ? COLORS.caramel : COLORS.espresso);
        const valueLines = doc.splitTextToSize(metric[1], width - 8).slice(0, 2);
        doc.text(valueLines, x + 4, y + 14, { lineHeightFactor: 1.05 });
      });
      y += height + 3;
    }

    function drawStatusCard(card) {
      ensureSpace(25);
      setFillColor(COLORS.peach);
      setDrawColor(COLORS.amber);
      doc.roundedRect(margin, y, contentWidth, 22, 2, 2, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      setTextColor(COLORS.espresso);
      doc.text(card.title, margin + 5, y + 7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      setTextColor(COLORS.muted);
      doc.text(doc.splitTextToSize(card.text, contentWidth - 10), margin + 5, y + 13, { lineHeightFactor: 1.25 });
      y += 25;
    }

    function drawTimeline(timeline) {
      drawSectionTitle(timeline.title);
      drawTextBlock(timeline.note, { fontSize: 8.7, color: COLORS.muted, after: 3 });
      const gap = 4;
      const cellWidth = (contentWidth - gap) / 2;
      const cellHeight = 12;
      timeline.rows.forEach((row, index) => {
        const column = index % 2;
        if (column === 0) ensureSpace(cellHeight + 2);
        const x = margin + column * (cellWidth + gap);
        setFillColor(index % 4 < 2 ? COLORS.oat : COLORS.cream);
        setDrawColor(COLORS.line);
        doc.setLineWidth(0.35);
        doc.roundedRect(x, y, cellWidth, cellHeight - 1, 2, 2, "FD");
        setFillColor(COLORS.amber);
        doc.circle(x + 5.5, y + 5.5, 1.6, "F");
        doc.setFont("courier", "bold");
        doc.setFontSize(9.2);
        setTextColor(COLORS.espresso);
        doc.text(row.time, x + 10, y + 7.2);
        setTextColor(COLORS.caramel);
        doc.text(row.carbs, x + cellWidth - 5, y + 7.2, { align: "right" });
        if (column === 1 || index === timeline.rows.length - 1) y += cellHeight;
      });
      y += 2;
    }

    function drawPills(labels, config = {}) {
      if (!labels.length) return;
      const fill = config.fill || COLORS.peach;
      const border = config.border || COLORS.amber;
      const color = config.color || COLORS.espresso;
      const maxX = pageWidth - margin;
      const pillHeight = 8;
      let x = margin;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.1);
      labels.forEach(label => {
        const width = Math.min(contentWidth, doc.getTextWidth(label) + 10);
        if (x + width > maxX) {
          x = margin;
          y += pillHeight + 2;
        }
        ensureSpace(pillHeight + 2);
        setFillColor(fill);
        setDrawColor(border);
        doc.setLineWidth(0.35);
        doc.roundedRect(x, y, width, pillHeight, 4, 4, "FD");
        setTextColor(color);
        doc.text(label, x + width / 2, y + 5.25, { align: "center" });
        x += width + 3;
      });
      y += pillHeight + 4;
    }

    function drawFormats(modelValue) {
      drawSectionTitle("Produktformen");
      drawTextBlock("FUELBOX-EMPFEHLUNG", { fontSize: 7.3, style: "bold", color: COLORS.caramel, after: 1 });
      if (modelValue.automaticFormats.length) drawPills(modelValue.automaticFormats);
      else drawTextBlock("Keine Sportverpflegung eingeplant.", { after: 2 });
      if (modelValue.personalFormats.length) {
        drawTextBlock("DEINE PERSÖNLICHE AUSWAHL", { fontSize: 7.3, style: "bold", color: COLORS.terracotta, after: 1 });
        drawPills(modelValue.personalFormats, { fill: COLORS.sand, border: COLORS.terracotta, color: COLORS.terracotta });
      }
      drawTextBlock(modelValue.productNote, { fontSize: 8.5, color: COLORS.muted, after: 1 });
    }

    function drawGuidance(rows, config = {}) {
      if (!config.guidePage) drawSectionTitle("Vorher, während und nachher");
      rows.forEach(([title, text], index) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const lines = doc.splitTextToSize(String(text || ""), contentWidth - 29);
        const bodyHeight = lines.length * lineHeightMm(9, 1.3);
        const height = Math.max(27, 15 + bodyHeight);
        ensureSpace(height + 4);

        setFillColor(index === 1 ? COLORS.peach : COLORS.cream);
        setDrawColor(index === 1 ? COLORS.amber : COLORS.line);
        doc.setLineWidth(0.35);
        doc.roundedRect(margin, y, contentWidth, height, 3, 3, "FD");
        setFillColor(index === 1 ? COLORS.caramel : COLORS.espresso);
        doc.roundedRect(margin + 5, y + 5, 13, 8, 4, 4, "F");
        doc.setFont("courier", "bold");
        doc.setFontSize(8);
        setTextColor(COLORS.white);
        doc.text(`0${index + 1}`, margin + 11.5, y + 10.4, { align: "center" });
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        setTextColor(COLORS.espresso);
        doc.text(title, margin + 23, y + 10.8);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        setTextColor(index === 1 ? COLORS.espresso : COLORS.muted);
        doc.text(lines, margin + 23, y + 17, { lineHeightFactor: 1.3 });
        y += height + 4;
      });
    }

    function drawHydrationPanel(hydration) {
      drawSectionTitle("Trinken");
      const leftWidth = 53;
      const rightX = margin + leftWidth;
      const rightWidth = contentWidth - leftWidth;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.7);
      const detailLines = doc.splitTextToSize(hydration.detail, rightWidth - 10);
      doc.setFontSize(8.1);
      const safetyLines = doc.splitTextToSize(hydration.safety, rightWidth - 10);
      const height = Math.max(42, 12 + detailLines.length * lineHeightMm(8.7, 1.25) + safetyLines.length * lineHeightMm(8.1, 1.25) + 7);
      ensureSpace(height + 4);

      setFillColor(COLORS.cream);
      setDrawColor(COLORS.line);
      doc.setLineWidth(0.35);
      doc.roundedRect(margin, y, contentWidth, height, 3, 3, "FD");
      setFillColor(COLORS.peach);
      doc.roundedRect(margin, y, leftWidth, height, 3, 3, "F");
      setDrawColor(COLORS.amber);
      doc.setLineWidth(0.8);
      doc.line(rightX, y + 4, rightX, y + height - 4);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.2);
      setTextColor(COLORS.caramel);
      doc.text("STARTBEREICH", margin + 6, y + 9);
      doc.setFont("courier", "bold");
      doc.setFontSize(hydration.summary.length > 12 ? 13 : 16);
      setTextColor(COLORS.espresso);
      doc.text(doc.splitTextToSize(hydration.summary, leftWidth - 12), margin + 6, y + 19, { lineHeightFactor: 1.05 });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.3);
      setTextColor(COLORS.muted);
      doc.text("Orientierung, kein Trinkziel", margin + 6, y + height - 7);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.2);
      setTextColor(COLORS.caramel);
      doc.text("SO KANNST DU ES EINORDNEN", rightX + 6, y + 9);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.7);
      setTextColor(COLORS.espresso);
      doc.text(detailLines, rightX + 6, y + 16, { lineHeightFactor: 1.25 });
      const detailBottom = y + 16 + detailLines.length * lineHeightMm(8.7, 1.25);
      setDrawColor(COLORS.line);
      doc.setLineWidth(0.25);
      doc.line(rightX + 6, detailBottom + 2, pageWidth - margin - 6, detailBottom + 2);
      doc.setFontSize(8.1);
      setTextColor(COLORS.muted);
      doc.text(safetyLines, rightX + 6, detailBottom + 7, { lineHeightFactor: 1.25 });
      y += height + 4;
    }

    function drawSafetyPanel(text) {
      drawSectionTitle("Sicher anwenden");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.4);
      const lines = doc.splitTextToSize(text, contentWidth - 22);
      const height = Math.max(22, 10 + lines.length * lineHeightMm(8.4, 1.3));
      ensureSpace(height + 4);
      setFillColor(COLORS.warningFill);
      setDrawColor(COLORS.warning);
      doc.setLineWidth(0.45);
      doc.roundedRect(margin, y, contentWidth, height, 3, 3, "FD");
      setFillColor(COLORS.warning);
      doc.circle(margin + 8, y + 9, 3.5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      setTextColor(COLORS.white);
      doc.text("!", margin + 8, y + 10.2, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.4);
      setTextColor(COLORS.espresso);
      doc.text(lines, margin + 16, y + 8, { lineHeightFactor: 1.3 });
      y += height + 4;
    }

    function drawPlanTakeaway(modelValue) {
      const height = 38;
      if (y + height + 10 > footerY - 5) return;
      y += 7;
      setFillColor(COLORS.espresso);
      doc.roundedRect(margin, y, contentWidth, height, 3, 3, "F");
      setFillColor(COLORS.amber);
      doc.roundedRect(margin + 7, y + 6, 30, 7, 3.5, 3.5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.2);
      setTextColor(COLORS.espresso);
      doc.text("MERKSATZ", margin + 22, y + 10.7, { align: "center" });

      const headline = modelValue.status === "fuel"
        ? modelValue.metrics.map(metric => metric[1]).join("  ·  ")
        : modelValue.statusCard.title;
      const detail = modelValue.status === "fuel" ? modelValue.guidance[1][1] : modelValue.statusCard.text;
      doc.setFont("courier", "bold");
      doc.setFontSize(modelValue.status === "fuel" ? 12 : 11);
      setTextColor(COLORS.white);
      doc.text(doc.splitTextToSize(headline, contentWidth - 14), margin + 7, y + 21, { lineHeightFactor: 1.05 });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.3);
      setTextColor(COLORS.peach);
      doc.text(doc.splitTextToSize(detail, contentWidth - 14).slice(0, 2), margin + 7, y + 29, { lineHeightFactor: 1.2 });
      y += height + 2;
    }

    function drawFinalCta(modelValue) {
      const height = 24;
      ensureSpace(height + 2);
      setFillColor(COLORS.espresso);
      doc.roundedRect(margin, y, contentWidth, height, 3, 3, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.2);
      setTextColor(COLORS.amber);
      doc.text("TEST. ANPASSEN. WIEDERHOLEN.", margin + 7, y + 7);
      doc.setFontSize(12.5);
      setTextColor(COLORS.white);
      doc.text("Deinen Plan jederzeit neu berechnen", margin + 7, y + 15);
      doc.setFont("courier", "bold");
      doc.setFontSize(8.4);
      setTextColor(COLORS.peach);
      doc.text("fuelbox.ch/pages/carb-planner", pageWidth - margin - 7, y + 14.5, { align: "right" });
      if (typeof doc.link === "function") doc.link(margin, y, contentWidth, height, { url: modelValue.plannerUrl });
      y += height + 2;
    }

    function drawWarning(warning) {
      if (!warning) return;
      const lines = doc.splitTextToSize(warning.text, contentWidth - 10);
      const height = 15 + lines.length * lineHeightMm(8.7, 1.25);
      ensureSpace(height + 4);
      setFillColor(COLORS.warningFill);
      setDrawColor(COLORS.warning);
      doc.roundedRect(margin, y, contentWidth, height, 2, 2, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      setTextColor(COLORS.warning);
      doc.text(warning.title, margin + 5, y + 7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.7);
      setTextColor(COLORS.espresso);
      doc.text(lines, margin + 5, y + 13, { lineHeightFactor: 1.25 });
      y += height + 2;
    }

    setFillColor(COLORS.espresso);
    doc.roundedRect(margin, y, contentWidth, 30, 3, 3, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    setTextColor(COLORS.white);
    doc.text("FuelBox", margin + 7, y + 10);
    doc.setFontSize(9);
    setTextColor(COLORS.amber);
    doc.text("TEST BEFORE YOU RACE.", margin + 7, y + 17);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    setTextColor(COLORS.white);
    doc.text(model.title, pageWidth - margin - 7, y + 11, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setTextColor(COLORS.peach);
    doc.text(model.createdLabel, pageWidth - margin - 7, y + 18, { align: "right" });
    y += 37;

    drawInfoRows(model.inputItems);
    if (model.statusCard) drawStatusCard(model.statusCard);
    else drawMetrics(model.metrics);
    drawWarning(model.warning);
    if (model.timeline.rows.length) drawTimeline(model.timeline);
    drawFormats(model);
    drawPlanTakeaway(model);
    addPage("guide");
    drawGuidance(model.guidance, { guidePage: true });
    drawHydrationPanel(model.hydration);
    drawSafetyPanel(model.disclaimer);
    drawFinalCta(model);

    const totalPages = doc.getNumberOfPages();
    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
      doc.setPage(pageNumber);
      setDrawColor(COLORS.line);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY, pageWidth - margin, footerY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.4);
      setTextColor(COLORS.muted);
      doc.text(model.plannerUrl, margin, footerY + 4.5);
      doc.text(`${pageNumber}/${totalPages}`, pageWidth - margin, footerY + 4.5, { align: "right" });
    }

    doc.setProperties({
      title: `FuelBox – ${model.title}`,
      subject: "Persönlicher Carb-Planner",
      author: "FuelBox",
      creator: "FuelBox Carb-Planner"
    });

    return {
      blob: doc.output("blob"),
      fileName: model.fileName,
      model,
      pageCount: totalPages
    };
  }

  globalThis.FuelboxCarbPlannerPdf = Object.freeze({ buildPdfModel, generatePdf });
})();
